import { updateMilestoneDateHistory } from '@gp/services/projectDetail';
import { fetchMilestoneEditServerTime, updateProjectMilestones } from '@gp/services/milestone';
import { formatDateStr, removeMockIdBatch } from '@gp/utils/common';
import {
  getMilestoneMap,
  getCorrectMilestoneList,
  calcNextMilestoneId,
  formatForecastFinancialsReq,
} from '@gp/pages/Pipeline/ProjectDetails/Milestone/utils';
import { BoolFlag } from '@gp/enums/common';
import { MilestoneDateStatus } from '@gp/enums/milestone';
import { DEFAULT_MILESTONE } from '@gp/pages/Pipeline/ProjectDetails/Milestone/services/milestone_templaces';

import type { IMilestoneReq } from '@gp/services/milestone';
import type { IMilestone, IMilestoneMap } from '@gp/pages/Pipeline/ProjectDetails/Milestone/types';
import type { IAddMilestoneFormModel } from '@gp/pages/Pipeline/ProjectDetails/Milestone/components/AddMilestoneModal';
import type { IUpdateMilestoneFormModel } from '@gp/pages/Pipeline/ProjectDetails/Milestone/components/UpdateMilestoneModal';
import type { IUpdateGRFormModel } from '@gp/pages/Pipeline/ProjectDetails/Milestone/components/UpdateGRModal';

interface IMilestoneServiceBaseParams {
  studioId?: string;
  projectId?: string;
  projectName?: string;
  milestoneId?: string;
}

interface IAddMilestoneParams extends IMilestoneServiceBaseParams {
  milestones?: IMilestone[] | null;
  formModel?: IAddMilestoneFormModel | null;
}

interface IUpdateMilestonesParams extends IMilestoneServiceBaseParams {
  milestoneMap?: IMilestoneMap;
  milestones?: IMilestone[] | null;
  formModel?: IUpdateMilestoneFormModel;
}

interface IUpdateMilestoneGRParams extends IMilestoneServiceBaseParams {
  milestoneMap?: IMilestoneMap;
  milestones?: IMilestone[] | null;
  formModel?: IUpdateGRFormModel;
}

interface IDeleteMilestoneParams extends IMilestoneServiceBaseParams {
  milestoneMap?: IMilestoneMap;
  milestones?: IMilestone[] | null;
}

const getCurrentTime = async (): Promise<string> => {
  try {
    const res: any = await fetchMilestoneEditServerTime({ projectId: '', studioId: '' });
    return res?.data || '';
  } catch (exception: any) {
    console.warn(exception);
    return '';
  }
};

/**
 * 组合Milestones集合与新增的Milestone
 * @param params 参数对象
 * @param params.milestones Milestones集合
 * @param params.milestone 新增的Milestone
 */
const addMilestone = (params: Pick<IAddMilestoneParams, 'milestones'> & { milestone: IMilestone }): IMilestone[] => {
  const { milestones, milestone } = params || {};
  // 当前Milestones集合是否为空集合
  const isMilestonesEmpty = !Array.isArray(milestones) || !milestones.length;
  // 添加的Milestones集合
  const addMilestones = milestone ? [milestone] : [];
  /**
   * 当前Milestones集合为空，即添加第一个Milestone
   * 当前Milestones集合非空，直接在末尾追加添加的Milestones集合
   */
  return isMilestonesEmpty ? addMilestones : [...milestones, ...addMilestones];
};

const updateMilestone = (milestones?: IMilestone[] | null, milestone?: Partial<IMilestone> | null): IMilestone[] => {
  if (!Array.isArray(milestones) || !milestones.length) return [];
  return milestones.map((item) => {
    if (item?.milestone_id === milestone?.milestone_id) return { ...item, ...milestone };
    return { ...item };
  });
};

const updateMilestones = (
  milestones?: IMilestone[] | null,
  milestoneMap?: IMilestoneMap,
  synchronizeMilestoneMap?: IMilestoneMap,
  partialMilestone?: Partial<IMilestone>,
  reason?: string,
  selectedKeys?: string[],
  currentTime?: string,
): IMilestoneReq[] => {
  if (!Array.isArray(milestones) || !milestones.length) return [];
  const partialMilestoneId = partialMilestone?.milestone_id;
  return milestones.map((milestone) => {
    const { milestone_id: milestoneId, date: milestoneDate, create_time: milestoneCreateTime } = milestone || {};
    const synchronizeMilestone = synchronizeMilestoneMap?.[milestoneId];
    let milestoneTmp1 = { ...milestone };
    if (milestoneId === partialMilestoneId) {
      milestoneTmp1 = {
        ...milestoneTmp1,
        ...partialMilestone,
        create_time: currentTime || milestoneCreateTime,
      };
    } else if (synchronizeMilestone) {
      const originalMilestoneDate = milestoneMap?.[milestoneId]?.date;
      const currentMilestoneDate = synchronizeMilestone?.date || milestoneDate;
      const isDateChange = originalMilestoneDate !== currentMilestoneDate;
      milestoneTmp1 = {
        ...milestoneTmp1,
        date: currentMilestoneDate,
        // @ts-ignore
        create_time: selectedKeys?.includes(milestoneId) || isDateChange ? currentTime : milestoneCreateTime,
      };
    }
    const milestoneTmp2 = formatMilestonesReqCommonMapper(milestoneTmp1, {
      milestoneMap,
      getReason: (payload) => {
        const { milestone_id: payloadMilestoneId, date: payloadMilestoneDate } = payload || {};
        const originalPayloadMilestoneDate = payloadMilestoneId ? milestoneMap?.[payloadMilestoneId]?.date : '';
        const isDateChange = originalPayloadMilestoneDate !== payloadMilestoneDate;
        if (!reason) return '';
        if (!payloadMilestoneId) return '';
        if (payloadMilestoneId === partialMilestoneId) return reason;
        if (selectedKeys?.includes(payloadMilestoneId) || isDateChange) return reason;
        return '';
      },
    });
    return { ...milestoneTmp2 };
  });
};

const deleteMilestone = (params: Pick<IDeleteMilestoneParams, 'milestoneId' | 'milestones'>): IMilestone[] => {
  const { milestoneId, milestones } = params || {};
  if (!Array.isArray(milestones) || !milestones.length) return [];
  if (!milestoneId) return [...milestones];
  return milestones.filter((milestone) => milestone?.milestone_id !== milestoneId);
};

const getListStr = (list: any): string => {
  if (!Array.isArray(list) || !list.length) return '';
  return JSON.stringify(list);
};

const formatMilestonesReqCommonMapper = (
  milestone: IMilestone,
  params?: {
    milestoneMap?: IMilestoneMap;
    getReason?: (milestone?: IMilestone) => string;
  },
): IMilestoneReq => {
  const { milestoneMap, getReason } = params || {};
  const {
    milestone_id: milestoneId,
    date,
    date_confirm: dateConfirm,
    gate_review: gateReview,
    gate_review_status: gateReviewStatus,
    action_items: actionItems,
    material: materials,
    man_month: manMonth,
    progress,
    is_del: isDel,
    key_milestones_financial: keyMilestonesFinancials,
    key_milestones_financial_forecast: keyMilestonesFinancialsForecast,
  } = milestone || {};
  const formattedDate = formatDateStr(date, 'YYYYMMDD');
  const originalDate = milestoneMap?.[milestoneId]?.date;
  const dateChange = originalDate && date && originalDate !== date ? 1 : 0;
  const formattedGateReview = Number.parseInt(gateReview, 10);
  const formattedGateReviewStatus = Number.parseInt(gateReviewStatus, 10);
  const formattedmanMonth = Number.parseInt(manMonth, 10);
  const formattedProgress = Number.parseInt(progress, 10);
  const formattedIsDel = Number.parseInt(isDel, 10);
  return {
    ...milestone,
    gate_review: Number.isNaN(formattedGateReview) ? null : formattedGateReview,
    gate_review_status: Number.isNaN(formattedGateReviewStatus) ? null : formattedGateReviewStatus,
    date: formattedDate,
    start_date: formattedDate,
    start_date_confirm: dateConfirm || MilestoneDateStatus.Confirm,
    milestone_date_change: dateChange,
    milestone_date_reason: getReason?.(milestone) || '',
    action_items: getListStr(actionItems),
    action_item_list: actionItems,
    material: getListStr(materials),
    man_month: Number.isNaN(formattedmanMonth) ? null : formattedmanMonth,
    progress: Number.isNaN(formattedProgress) ? null : formattedProgress,
    is_del: Number.isNaN(formattedIsDel) ? null : formattedIsDel,
    key_milestones_financial: removeMockIdBatch(keyMilestonesFinancials),
    key_milestones_financial_forecast: formatForecastFinancialsReq(keyMilestonesFinancialsForecast),
  };
};

/**
 * 格式化Milestone集合字段参数
 * date：组件内格式为YYYY-MM-DD，转换为YYYYMMDD格式，接口需要
 * action_items: 组件内格式为IGRAction[]，转为string，接口需要
 * material: 组件内格式为IMaterial[]，转为为string，接口需要
 * @param milestones Milestone集合
 * @param params 参数对象
 * @param params.milestoneMap milestone映射,键为milestone的id，值为milestone
 * @return 接口入参需要的Milestone集合格式
 */
const formatMilestonesReq = (
  milestones?: IMilestone[] | null,
  params?: { milestoneMap?: IMilestoneMap },
): IMilestoneReq[] => {
  if (!Array.isArray(milestones) || !milestones.length) return [];
  return milestones.map((milestone) => {
    return { ...formatMilestonesReqCommonMapper(milestone, params) };
  });
};

export default class MilestoneService {
  static async addHistory(
    projectId?: string,
    projectName?: string,
    milestonesReq?: IMilestoneReq[],
    milestonesRes?: IMilestoneReq[],
  ) {
    try {
      if (!projectId) throw new Error('an error occured in updateProjectMilestones: project_id is required');
      await updateMilestoneDateHistory({
        project_id: projectId,
        project: projectName,
        milestoneList: getCorrectMilestoneList(milestonesReq, milestonesRes),
      });
    } catch (exception) { }
  }

  static async addMilestone(params: IAddMilestoneParams & { nextMilestoneId?: string }) {
    const { studioId, projectId, projectName, milestones, formModel, nextMilestoneId } = params || {};
    const {
      milestone: milestoneName = '',
      // is_gr: milestoneIsGR = BoolFlag.False,
      // is_launch: milestoneIsLaunch = BoolFlag.False,
      category = '',
      date: milestoneDate = '',
      date_confirm: milestoneDateConfirm = MilestoneDateStatus.Confirm,
    } = formModel || {};
    if (!studioId) throw new Error('an error occured in updateProjectMilestones: studio_id is required');
    if (!projectId) throw new Error('an error occured in updateProjectMilestones: project_id is required');
    // 要添加的Milestone
    const milestone: IMilestone = {
      ...DEFAULT_MILESTONE,
      project_id: String(projectId),
      milestone: milestoneName?.trim() || '',
      // is_gr: milestoneIsGR,
      // is_launch: milestoneIsLaunch,
      category: category,
      date: milestoneDate,
      date_confirm: milestoneDateConfirm,
      create_time: await getCurrentTime(),
    };
    // 添加Milestone后的Milestones集合
    const addedMilestones = addMilestone({
      milestones,
      milestone,
    });
    // 格式化参数后的Milestones集合
    const formattedMilestones = formatMilestonesReq(addedMilestones);
    // 调接口更新Milestones集合
    const addRes = await updateProjectMilestones({
      studio_id: studioId,
      project_id: projectId,
      next_milestone: nextMilestoneId || '',
      milestoneList: formattedMilestones,
      editing_milestone_id: '',
    });
    if (addRes?.code !== 200)
      throw new Error(`an error occured in updateProjectMilestones: ${addRes?.msg || 'unknow error'}`);
    MilestoneService.addHistory(projectId, projectName, formattedMilestones, addRes.req?.milestoneList);
  }

  static async updateMilestones(params: IUpdateMilestonesParams & { nextMilestoneId?: string }) {
    const { studioId, projectId, projectName, milestoneMap, milestones, milestoneId, formModel, nextMilestoneId } =
      params || {};
    const {
      milestone: milestoneName = '',
      // is_gr: milestoneIsGR = BoolFlag.False,
      // is_launch: milestoneIsLaunch = BoolFlag.False,
      date: milestoneDate = '',
      date_confirm: milestoneDateConfirm = MilestoneDateStatus.Confirm,
      reason,
      category,
      synchronize_data: synchronizeData,
    } = formModel || {};

    const { selectedKeys, milestones: synchronizeMilestones } = synchronizeData || {};
    const activeMilestone = milestoneMap?.[milestoneId as string];
    if (!studioId) throw new Error('an error occured in updateProjectMilestones: studio_id is required');
    if (!projectId) throw new Error('an error occured in updateProjectMilestones: project_id is required');
    const synchronizeMilestoneMap = getMilestoneMap(synchronizeMilestones);
    const currentTime = await getCurrentTime();
    const milestonesTmp = updateMilestones(
      milestones,
      milestoneMap,
      synchronizeMilestoneMap,
      {
        milestone_id: activeMilestone?.milestone_id || '',
        milestone: milestoneName,
        // is_gr: milestoneIsGR,
        // is_launch: milestoneIsLaunch,
        category: category,
        date: milestoneDate,
        date_confirm: milestoneDateConfirm,
      },
      reason,
      selectedKeys,
      currentTime,
    );
    // 调接口更新Milestones集合
    const updateRes = await updateProjectMilestones({
      studio_id: studioId,
      project_id: projectId,
      next_milestone: nextMilestoneId || '',
      milestoneList: milestonesTmp,
      editing_milestone_id: milestoneId,
    });
    if (updateRes?.code !== 200)
      throw new Error(`an error occured in updateProjectMilestones: ${updateRes?.msg || 'unknow error'}`);
    MilestoneService.addHistory(projectId, projectName, milestonesTmp, updateRes.req?.milestoneList);
  }

  static async updateMilestoneGR(params: IUpdateMilestoneGRParams & { nextMilestoneId?: string }) {
    const { studioId, projectId, projectName, milestoneMap, milestones, milestoneId, formModel, nextMilestoneId } =
      params || {};
    const activeMilestone = milestoneMap?.[milestoneId as string];
    if (!studioId) throw new Error('an error occured in updateProjectMilestones: studio_id is required');
    if (!projectId) throw new Error('an error occured in updateProjectMilestones: project_id is required');
    // 更新后的Milestones集合
    const updatedMilestones = updateMilestone(milestones, {
      ...activeMilestone,
      ...formModel,
      key_milestones_financial: formModel?.key_milestones_financial,
      key_milestones_financial_forecast: formModel?.key_milestones_financial_forecast,
      create_time: await getCurrentTime(),
    });
    // 格式化参数后的Milestones集合
    const formattedMilestones = formatMilestonesReq(updatedMilestones, { milestoneMap });
    // 调接口更新Milestones集合
    const updateRes = await updateProjectMilestones({
      studio_id: studioId,
      project_id: projectId,
      next_milestone: nextMilestoneId || '',
      milestoneList: formattedMilestones,
      editing_milestone_id: milestoneId,
    });
    if (updateRes?.code !== 200)
      throw new Error(`an error occured in updateProjectMilestones: ${updateRes?.msg || 'unknow error'}`);
    MilestoneService.addHistory(projectId, projectName, formattedMilestones, updateRes.req?.milestoneList);
  }

  static async deleteMilestone(params: IDeleteMilestoneParams & { nextMilestoneId?: string }) {
    const { studioId, projectId, projectName, milestoneMap, milestones, milestoneId, nextMilestoneId } = params || {};
    if (!studioId) throw new Error('an error occured in updateProjectMilestones: studio_id is required');
    if (!projectId) throw new Error('an error occured in updateProjectMilestones: project_id is required');
    // 本次删除操作后，新Next Milestone Id
    const nextMilestoneIdNew = calcNextMilestoneId({
      milestones,
      nextMilestoneId,
      deleteMilestoneId: milestoneId,
    });
    // 更新后的Milestones集合
    const updatedMilestones = deleteMilestone({ milestoneId, milestones });
    // 格式化参数后的Milestones集合
    const formattedMilestones = formatMilestonesReq(updatedMilestones, { milestoneMap });
    // 调接口更新Milestones集合
    const updateRes = await updateProjectMilestones({
      studio_id: studioId,
      project_id: projectId,
      next_milestone: nextMilestoneIdNew,
      milestoneList: formattedMilestones,
      editing_milestone_id: milestoneId,
    });
    if (updateRes?.code !== 200)
      throw new Error(`an error occured in updateProjectMilestones: ${updateRes?.msg || 'unknow error'}`);
    MilestoneService.addHistory(projectId, projectName, formattedMilestones, updateRes.req?.milestoneList);
  }
}
